import { useCallback, useMemo, useState } from 'react';
import { notification } from 'antd';
import { submitConfigureDeployment } from './submitConfigureDeployment.js';
import {
  filterEligibleTrainings,
  findTrainingForUsecaseVersion,
} from './eligibleTrainings.js';

/**
 * Shared "Download model" → "Configure deployment" flow used by every surface
 * that lets the user download / deploy a trained model (Models page rows,
 * task-page kebab, Model Details drawer on the task page, …). Pair with
 * ``ConfigureDeploymentFlowModals`` so the modal chain is not copied.
 *
 * Owns the modal-chain state (format picker → configure modal → duplicate-ECR
 * confirm) and the submit path that reports progress through
 * ``setProgressStatus`` so the global FloatingProgressBar reflects it.
 *
 * @param {object} params
 * @param {import('react').Dispatch<unknown>} params.setProgressStatus
 * @param {(training: object) => void} [params.onDeploySuccess] — optimistic UI
 *   update / refetch after a deploy request is accepted.
 * @param {object[]|null} [params.trainings] — enriched trainings list when the
 *   caller already has one (Models page). Feeds the configure modal's Add-Task
 *   picker and version selects.
 * @param {() => Promise<object[]>} [params.loadTrainings] — lazy loader used
 *   when the caller has no trainings list (task page drawer). Invoked once,
 *   when the download flow first opens.
 */
export function useConfigureDeploymentFlow({
  setProgressStatus,
  onDeploySuccess,
  trainings = null,
  loadTrainings = null,
}) {
  // Format-picker modal ("Download model": raw files / Docker / registry
  // pushes). ``downloadModalMode`` selects which option groups the picker
  // shows: 'all' for the Download entry point, 'push' for Push-model.
  const [downloadModalTraining, setDownloadModalTraining] = useState(null);
  const [downloadModalArtifactType, setDownloadModalArtifactType] = useState('zip');
  const [downloadModalMode, setDownloadModalMode] = useState('all');

  // Configure Deployment modal state — shared by Docker + ZIP downloads and
  // live deploys. ``artifactType`` carries the format the user picked in
  // ModelDownloadModal so the modal + submit can package the right artifact.
  const [configureModal, setConfigureModal] = useState({
    open: false,
    mode: null,
    training: null,
    artifactType: 'docker',
    instancesCapErrorByTaskKey: {},
    focusedTaskKey: null,
  });

  const [duplicatePushConfirm, setDuplicatePushConfirm] = useState(null);
  const [duplicatePushSubmitting, setDuplicatePushSubmitting] = useState(false);

  const [fetchedTrainings, setFetchedTrainings] = useState(null);

  const resolvedTrainings = useMemo(
    () => trainings ?? fetchedTrainings ?? [],
    [trainings, fetchedTrainings]
  );

  const ensureTrainingsLoaded = useCallback(() => {
    if (trainings || fetchedTrainings || !loadTrainings) return;
    loadTrainings()
      .then((list) => setFetchedTrainings(Array.isArray(list) ? list : []))
      .catch(() => setFetchedTrainings([]));
  }, [trainings, fetchedTrainings, loadTrainings]);

  const handleDownloadTraining = useCallback(
    (training) => {
      ensureTrainingsLoaded();
      setDownloadModalMode('all');
      setDownloadModalArtifactType('zip');
      setDownloadModalTraining(training);
    },
    [ensureTrainingsLoaded]
  );

  const handlePushTraining = useCallback(
    (training) => {
      ensureTrainingsLoaded();
      setDownloadModalMode('push');
      setDownloadModalArtifactType('ecr');
      setDownloadModalTraining(training);
    },
    [ensureTrainingsLoaded]
  );

  const handleDownloadModalCancel = useCallback(() => {
    setDownloadModalTraining(null);
    setDownloadModalMode('all');
    setDownloadModalArtifactType('zip');
  }, []);

  const handleConfirmModelDownload = useCallback(
    (downloadOptions) => {
      if (!downloadModalTraining) {
        return;
      }
      // Both formats (ZIP raw files and Docker image) go through the Configure
      // Deployment modal so the operator can pick the infra + long-input +
      // prediction-strategy + instances knobs before we fire the actual
      // download request. The chosen ``artifactType`` is threaded through so
      // the modal + submit package the right artifact.
      //
      // Callers like the task-page drawer only carry a sparse version object,
      // so merge in the full trainings row (trained prediction strategy,
      // long-text flag, …) when we have it — that's what seeds the configure
      // modal's runtime defaults on the Models page.
      const fullRow = findTrainingForUsecaseVersion(
        resolvedTrainings,
        downloadModalTraining.llm_usecase_id,
        downloadModalTraining.version
      );
      const target = fullRow
        ? { ...fullRow, ...downloadModalTraining }
        : downloadModalTraining;
      const artifactType = ['docker', 'ecr', 'gcr', 'dockerhub', 'acr'].includes(
        downloadOptions?.artifactType
      )
        ? downloadOptions.artifactType
        : 'zip';
      setDownloadModalTraining(null);
      setDownloadModalArtifactType(artifactType);
      setConfigureModal({
        open: true,
        mode: 'download',
        training: target,
        artifactType,
        instancesCapErrorByTaskKey: {},
        focusedTaskKey: null,
      });
    },
    [downloadModalTraining, resolvedTrainings]
  );

  const closeConfigureModal = useCallback(() => {
    setConfigureModal({
      open: false,
      mode: null,
      training: null,
      artifactType: 'docker',
      instancesCapErrorByTaskKey: {},
      focusedTaskKey: null,
    });
  }, []);

  const handleConfigureCancel = useCallback(() => {
    closeConfigureModal();
  }, [closeConfigureModal]);

  const handleConfigureBack = useCallback(() => {
    if (configureModal.mode !== 'download' || !configureModal.training) {
      closeConfigureModal();
      return;
    }
    const target = configureModal.training;
    setDownloadModalArtifactType(configureModal.artifactType || 'zip');
    closeConfigureModal();
    setDownloadModalTraining(target);
  }, [
    configureModal.mode,
    configureModal.training,
    configureModal.artifactType,
    closeConfigureModal,
  ]);

  const runConfigureSubmit = useCallback(
    async ({ mode, sharedConfig, tasks, artifactType, forcePush }) => {
      try {
        await submitConfigureDeployment({
          mode,
          sharedConfig,
          tasks,
          artifactType,
          setProgressStatus,
          onDeploySuccess,
          forcePush,
        });
        closeConfigureModal();
      } catch (err) {
        if (err?.isInstancesCapError && err.taskKey) {
          setConfigureModal((prev) => ({
            ...prev,
            instancesCapErrorByTaskKey: {
              ...prev.instancesCapErrorByTaskKey,
              [err.taskKey]: err.message,
            },
            focusedTaskKey: err.taskKey,
          }));
          return;
        }
        // Image already pushed before: ask the operator to confirm an
        // overriding push instead of silently failing.
        if (err?.isDuplicatePush) {
          setDuplicatePushConfirm({
            mode,
            sharedConfig,
            tasks,
            artifactType,
            previousPush: err.previousPush || null,
          });
          return;
        }
        notification.error({
          message: mode === 'download' ? 'Download Failed' : 'Deployment Failed',
          description: err?.message || 'Request failed',
          duration: 6,
        });
      }
    },
    [setProgressStatus, onDeploySuccess, closeConfigureModal]
  );

  const handleConfigureConfirm = useCallback(
    async ({ mode, sharedConfig, tasks, artifactType }) => {
      if (!tasks?.length) {
        closeConfigureModal();
        return;
      }
      await runConfigureSubmit({ mode, sharedConfig, tasks, artifactType, forcePush: false });
    },
    [runConfigureSubmit, closeConfigureModal]
  );

  const handleDuplicatePushCancel = useCallback(() => {
    if (duplicatePushSubmitting) return;
    setDuplicatePushConfirm(null);
  }, [duplicatePushSubmitting]);

  const handleDuplicatePushConfirm = useCallback(async () => {
    if (!duplicatePushConfirm || duplicatePushSubmitting) return;
    const { mode, sharedConfig, tasks, artifactType } = duplicatePushConfirm;
    setDuplicatePushSubmitting(true);
    try {
      setDuplicatePushConfirm(null);
      await runConfigureSubmit({ mode, sharedConfig, tasks, artifactType, forcePush: true });
    } finally {
      setDuplicatePushSubmitting(false);
    }
  }, [duplicatePushConfirm, duplicatePushSubmitting, runConfigureSubmit]);

  const handleClearInstancesCapErrorForTask = useCallback((taskKey) => {
    setConfigureModal((prev) => {
      if (!prev.instancesCapErrorByTaskKey[taskKey]) return prev;
      const next = { ...prev.instancesCapErrorByTaskKey };
      delete next[taskKey];
      return { ...prev, instancesCapErrorByTaskKey: next };
    });
  }, []);

  const handleFocusedTaskKeyChange = useCallback((taskKey) => {
    setConfigureModal((prev) => ({ ...prev, focusedTaskKey: taskKey }));
  }, []);

  const eligibleTrainings = useMemo(
    () => filterEligibleTrainings(resolvedTrainings, configureModal.mode, []),
    [resolvedTrainings, configureModal.mode]
  );

  return {
    // Format-picker modal
    downloadModalTraining,
    downloadModalArtifactType,
    downloadModalMode,
    handleDownloadTraining,
    handlePushTraining,
    handleDownloadModalCancel,
    handleConfirmModelDownload,
    // Configure Deployment modal
    configureModal,
    eligibleTrainings,
    handleConfigureCancel,
    handleConfigureBack,
    handleConfigureConfirm,
    handleClearInstancesCapErrorForTask,
    handleFocusedTaskKeyChange,
    // Duplicate-ECR-push confirm modal
    duplicatePushConfirm,
    duplicatePushSubmitting,
    handleDuplicatePushCancel,
    handleDuplicatePushConfirm,
  };
}
